import colors from 'common/styles/colors';
import i18n from 'i18n/config';
import { formatAssetSetupData, formatDefectsTrendData, formatEventsData, formatEventTrendData, formatPlantTrendData, formatUnreadEventsData, formatCostSavedByMachineData } from 'home/Machines/utils';
import { navToActiviyPage, navToAssetsPage, navToDevicesPage, navToMachineActivityPage } from '../helpers/helpers';

export const widgetConstants = {
  GET_WIDGET_METADATA_REQUEST: 'GET_WIDGET_METADATA_REQUEST',
  GET_WIDGET_METADATA_SUCCESS: 'GET_WIDGET_METADATA_SUCCESS',
  GET_WIDGET_METADATA_FAILURE: 'GET_WIDGET_METADATA_FAILURE',

  GET_WIDGET_DATA_REQUEST: 'GET_WIDGET_DATA_REQUEST',
  GET_WIDGET_DATA_SUCCESS: 'GET_WIDGET_DATA_SUCCESS',
  GET_WIDGET_DATA_FAILURE: 'GET_WIDGET_DATA_FAILURE',
};

const VFD_OPTIONS = {
  VFD_ROTATING: 'vfd_rotating',
  NON_VFD_ROTATING: 'non_vfd_rotating',
  OTHER: 'other'
};
export const getCustomLabels = (t = i18n.t.bind(i18n)) => ({
  vfd_rotating: t('widgets.customLabels.vfdRotating', { defaultValue: 'VFD Rotating' }),
  non_vfd_rotating: t('widgets.customLabels.nonVfdRotating', { defaultValue: 'Non-VFD Rotating' }),
  pole_count_speed: t('widgets.customLabels.poleCountRatedSpeed', { defaultValue: 'Pole Count / Rated Speed' }),
});

// Deprecated: prefer getWidgetCalendarOptions(t).
// Kept as a function alias (not a computed array) to avoid "frozen" translations.
export const getWidgetCalendarOptions = (t = i18n.t.bind(i18n)) => ([
  { text: '1D', value: 1 },
  { text: '7D', value: 7 },
  { text: '14D', value: 14 },
  { text: '30D', value: 30 },
  { text: '90D', value: 90 },
  { text: t('common.all', { defaultValue: 'All' }), value: 'all' },
]);

// Deprecated: prefer getCustomLabels(t).
// Kept as a function alias (not a computed object) to avoid "frozen" translations.
export const customLabels = getCustomLabels;
export const widgetCalendarOptions = getWidgetCalendarOptions;

const defectWidgetOptions = {
  orientation: 'horizontal',
  isInteractive: true,
  isStacked: false,
  chartArea: { width: '75%', left: '15%' },
  dataFormatter: formatDefectsTrendData,
  yLabelFontSize: 8,
  hasCustomTooltip: true,
  colors: [
    '#FF9764',
    '#FFE25F',
    '#37B6B8',
    '#05FBA1',
    '#4babfa',
    '#A6FF36',
    '#ff8282',
    '#FB056A',
    '#b14aff',
    '#ff4a4a',
    colors.greyXD
  ],
  sortOrder: ['total_count'],
  handleClick: (id, filterOptions, machineId) => (data, switchAccount) => {
    if (machineId) {
      navToMachineActivityPage(id, data, filterOptions, machineId);
    } else {
      navToActiviyPage(id, data, filterOptions, switchAccount);
    }
  }
};

export const keyMetadataMap = {
  device_reachability_breakdown: {
    formatData: true,
    colors: [colors.normal, colors.critical, colors.unknown],
    sortOrder: ['reachable', 'unreachable', 'unassigned'],
    filters: [
      { name: 'last_measurement', op: 'in', value: [false] },
      { name: 'last_measurement', op: 'in', value: [true] },
      { name: 'last_measurement', op: 'in', value: ['unassigned'] }
    ],
    handleClick: (id, filterOptions) => (data, index) => navToDevicesPage(id, index, filterOptions)
  },
  device_battery_level_breakdown: {
    formatData: true,
    colors: [colors.normal, colors.warning, colors.critical, colors.unknown],
    sortOrder: ['> 20%', '< 20%', '< 10%', 'unknown'],
    legendFilters: [
      { name: 'battery_level', op: 'in', value: [1] },
      { name: 'battery_level', op: 'in', value: [2] },
      { name: 'battery_level', op: 'in', value: [3] },
      { name: 'battery_level', op: 'in', value: [null] }
    ],
    handleClick: (id, filterOptions) => (data, index) => navToDevicesPage(id, index, filterOptions)
  },
  device_wifi_strength_breakdown: {
    formatData: true,
    colors: [colors.normal, colors.slight, colors.moderate, colors.warning, colors.critical, colors.unknown],
    sortOrder: ['strong', 'good', 'normal', 'weak', 'very weak', 'unknown'],
    filters: [
      { name: 'wifi_signal', op: 'in', value: [4] },
      { name: 'wifi_signal', op: 'in', value: [3] },
      { name: 'wifi_signal', op: 'in', value: [2] },
      { name: 'wifi_signal', op: 'in', value: [1] },
      { name: 'wifi_signal', op: 'in', value: [0] },
      { name: 'wifi_signal', op: 'in', value: [null] }
    ],
    handleClick: (id, filterOptions) => (data, index) => navToDevicesPage(id, index, filterOptions)
  },
  firmware_version_breakdown: {
    formatData: true,
    colors: [colors.normal, colors.slight, colors.moderate],
    isInteractive: true,
    handleClick: (id, filterOptions) => (data, switchAccount) => navToDevicesPage(id, data, filterOptions, switchAccount)
  },
  device_unreachable_trend: {
    colors: [colors.green],
  },
  missed_measurements_trend: {
    orientation: 'vertical',
    colors: [colors.greyXD],
  },
  asset_health_scores_breakdown: {
    filterName: 'health_score',
    hasSublevels: true,
    // path in redux store
    sublevelPath: 'currentAccount.preferences.machine_condition_levels',
    5: {
      resourceName: 'Machines',
      formatData: true,
      colors: [
        colors.normal,
        colors.slight,
        colors.moderate,
        colors.warning,
        colors.critical,
        colors.unknown
      ],
      sortOrder: ['< 10', '< 8', '< 6', '< 4', '< 2', 'unknown'],
      filters: [
        { text: '< 10', value: [8, 10] },
        { text: '< 8', value: [6, 8] },
        { text: '< 6', value: [4, 6] },
        { text: '< 4', value: [2, 4] },
        { text: '< 2', value: [0, 2] },
        { text: 'unknown', value: 'unknown' }
      ],
      handleClick: (id, filterOptions) => (data, index) => navToAssetsPage(id, index, filterOptions, 'health_score')
    },
    3: {
      resourceName: 'Machines',
      formatData: true,
      colors: [
        colors.normal,
        colors.warning,
        colors.critical,
        colors.unknown
      ],
      sortOrder: ['< 10', '< 7', '< 4', 'unknown'],
      filters: [
        { text: '< 10', value: [7, 10] },
        { text: '< 7', value: [4, 7] },
        { text: '< 4', value: [0, 4] },
        { text: 'unknown', value: 'unknown' }
      ],
      handleClick: (id, filterOptions) => (data, index) => navToAssetsPage(id, index, filterOptions, 'health_score')
    }
  },
  vfd_breakdown: {
    filterName: 'vfd',
    formatData: true,
    sortOrder: [
      VFD_OPTIONS.VFD_ROTATING,
      VFD_OPTIONS.NON_VFD_ROTATING,
      VFD_OPTIONS.OTHER
    ],
    colors: [colors.normal, colors.slight, colors.unknown],
    filters: [
      { text: VFD_OPTIONS.VFD_ROTATING, value: VFD_OPTIONS.VFD_ROTATING },
      { text: VFD_OPTIONS.NON_VFD_ROTATING, value: VFD_OPTIONS.NON_VFD_ROTATING },
      { text: VFD_OPTIONS.OTHER, value: VFD_OPTIONS.OTHER }
    ],
    handleClick: (id, filterOptions) => (data, index) => navToAssetsPage(id, index, filterOptions, 'vfd')
  },
  alarm_review_and_action_report: {
    filterName: 'alarms_review_and_action_report',
    isInteractive: true,
    filters: [
      { text: 'machines_with_alarm_but_no_task', value: 'machines_with_alarm_but_no_task' },
      { text: 'machines_with_alarm_task_but_no_updates', value: 'machines_with_alarm_task_but_no_updates' },
      { text: 'machines_with_recent_open_alarms', value: 'machines_with_recent_open_alarms' },
      { text: 'machines_with_task_but_no_alarm', value: 'machines_with_task_but_no_alarm' }
    ],
    handleClick: (id, filterOptions) => (data, switchAccount) => navToAssetsPage(id, data, filterOptions, 'alarm_review_and_action_report', switchAccount)
  },
  open_events_breakdown: {
    resourceName: 'Machines',
    isStacked: false,
    orientation: 'vertical',
    addTitleRow: true,
    chartArea: { width: '80%' },
    filterName: 'events',
    dataFormatter: formatEventsData,
    colors: [
      '#37B6B8',
      '#05FBA1',
      '#4babfa',
      '#A6FF36',
    ],
    handleClick: (id, filterOptions) => (data, index) => navToAssetsPage(id, index, filterOptions, 'events')
  },
  incomplete_asset_setup_breakdown: {
    resourceName: 'Machines',
    isStacked: true,
    sortOrder: {
      baseline: ['partially_configured', 'not_configured'],
      alarms: ['partially_configured', 'not_configured'],
      speed_info: ['pole_count_speed', 'line_frequency', 'gear_ratio', 'speed_ratio'],
      others: []
    },
    filterName: 'asset_setup',
    colors: [
      // for baseline
      colors.slight, colors.moderate,
      // for alarm thresholds
      '#FF9764', '#FFE25F',
      // for speed info
      '#37B6B8', '#05FBA1', '#4babfa', '#A6FF36',
      // for others
      '#ff8282', '#FB056A', '#b14aff', '#ff4a4a',
    ],
    chartArea: { width: '75%', left: '20%', bottom: '30%' },
    dataFormatter: formatAssetSetupData,
    yLabelFontSize: 10,
    handleClick: (id, filterOptions) => (data, index) => navToAssetsPage(id, index, filterOptions, 'asset_setup')
  },
  recent_assets_requiring_attention: {
    // eslint-disable-next-line global-require
    component: () => require('home/Dashboard/components/machines_list/AttentionMachinesPage').default,
    filters: {
      roles: ['assembler', 'admin', 'technician', 'manager', 'developer'],
      filters: [{ name: 'attention_criteria', op: 'in', value: ['tasks'] }]
    }
  },
  plant_asset_health: {
    hasSublevels: true,
    // path in redux store
    sublevelPath: 'currentAccount.preferences.machine_condition_levels',
    5: {
      resourceName: 'Machines',
      formatData: true,
      colors: [
        colors.normal,
        colors.slight,
        colors.moderate,
        colors.warning,
        colors.critical,
        colors.unknown
      ],
      sortOrder: ['good', 'slight', 'moderate', 'warning', 'critical', 'unknown'],
      handleClick: (id, filterOptions) => data => navToAssetsPage(id, data, filterOptions)
    },
    3: {
      resourceName: 'Machines',
      formatData: true,
      colors: [
        colors.normal,
        colors.warning,
        colors.critical,
        colors.unknown
      ],
      sortOrder: ['good', 'warning', 'critical', 'unknown'],
      handleClick: (id, filterOptions) => data => navToAssetsPage(id, data, filterOptions)
    }
  },
  plant_asset_health_trend: {
    hasSublevels: true,
    // path in redux store
    sublevelPath: 'currentAccount.preferences.machine_condition_levels',
    3: {
      orientation: 'vertical',
      isInteractive: false,
      chartArea: { width: '80%', bottom: '30%' },
      dataFormatter: formatPlantTrendData,
      conditionLabels: ['Good', 'Warning', 'Critical', 'Unknown'],
      colors: [
        colors.normal,
        colors.warning,
        colors.critical,
        colors.unknown
      ],
    },
    5: {
      orientation: 'vertical',
      isInteractive: false,
      chartArea: { width: '80%', bottom: '30%' },
      dataFormatter: formatPlantTrendData,
      conditionLabels: ['Good', 'Slight', 'Moderate', 'Warning', 'Critical', 'Unknown'],
      colors: [
        colors.normal,
        colors.slight,
        colors.moderate,
        colors.warning,
        colors.critical,
        colors.unknown
      ],
    }
  },
  current_plant_utilization: {
    resourceName: 'Machines',
    formatData: true,
    colors: [colors.green, colors.greenXL, colors.greyL],
    sortOrder: [
      'ON', 'OFF', 'NA'
    ],
    handleClick: (id, filterOptions) => data => navToAssetsPage(id, data, filterOptions)
  },
  recent_activity: {
    // eslint-disable-next-line global-require
    component: () => require('home/Dashboard/components/activity_list/ActivityPage').default
  },
  open_closed_events_trend: {
    orientation: 'vertical',
    isInteractive: false,
    chartArea: { width: '80%', bottom: '30%' },
    dataFormatter: formatEventTrendData,
    colors: [
      colors.green,
      colors.unknown
    ]
  },
  open_closed_events: {
    sortOrder: ['open', 'closed'],
    isInteractive: true,
    handleClick: (id, filterOptions) => (data, switchAccount) => navToActiviyPage(id, data, filterOptions, switchAccount)
  },
  diagnostic_rule_result_count: {
    sortOrder: ['open', 'review_pending'],
    isInteractive: true,
    handleClick: (id, filterOptions) => (data, switchAccount) => navToActiviyPage(id, data, filterOptions, switchAccount)
  },
  active_inactive_tags: {
    sortOrder: ['active', 'inactive'],
  },
  open_closed_tasks: {
    sortOrder: ['open', 'completed'],
    isInteractive: true,
    handleClick: (id, filterOptions) => (data, switchAccount) => navToActiviyPage(id, data, filterOptions, switchAccount),
    dataFormatter: (res, metadata) => {
      if (res && res.asset_maintenance) {
        const instrumentation = res.instrumentation || {};
        return [
          {
            asset_maintenance_open: res.asset_maintenance.open || 0,
            instrumentation_open: instrumentation.open || 0,
            asset_maintenance_completed: res.asset_maintenance.completed || 0,
            instrumentation_completed: instrumentation.completed || 0
          },
          null
        ];
      }
      return null;
    },
    orgSortOrder: [
      'asset_maintenance_open',
      'instrumentation_open',
      'asset_maintenance_completed',
      'instrumentation_completed'
    ]
  },
  org_sites_by_site: {
    // eslint-disable-next-line global-require
    component: () => require('home/Organisation/widgets/OrgSitesBySite').default
  },
  org_total_defects_by_site: {
    // eslint-disable-next-line global-require
    component: () => require('home/Organisation/widgets/OrgTotalDefectsBySite').default
  },
  org_good_health_pct: {
    sortOrder: ['good_health_pct'],
    numberSize: 32,
    labelSize: 11,
    numberPadding: 8,
    dataFormatter: (res) => {
      if (!res) return null;
      return [
        { good_health_pct: `${res.good_health_pct != null ? res.good_health_pct : 0}%` },
        null
      ];
    }
  },
  org_asset_instrumentation_tasks_by_site: {
    // eslint-disable-next-line global-require
    component: () => require('home/Organisation/widgets/OrgAssetInstrumentationTasksBySite').default
  },
  org_unread_unanalysed_by_site: {
    // eslint-disable-next-line global-require
    component: () => require('home/Organisation/widgets/OrgUnreadUnanalysedBySite').default
  },
  org_open_events_count: {
    sortOrder: ['open_events'],
    numberSize: 32,
    labelSize: 11,
    numberPadding: 8
  },
  org_sensors_offline_pct: {
    sortOrder: ['sensors_offline_pct'],
    numberSize: 32,
    labelSize: 11,
    numberPadding: 8,
    dataFormatter: (res) => {
      if (!res) return null;
      return [
        { sensors_offline_pct: `${res.sensors_offline_pct != null ? res.sensors_offline_pct : 0}%` },
        null
      ];
    }
  },
  org_fleet_health_by_site: {
    // eslint-disable-next-line global-require
    component: () => require('home/Organisation/widgets/OrgFleetHealthBySite').default
  },
  org_open_events_by_site: {
    // eslint-disable-next-line global-require
    component: () => require('home/Organisation/widgets/OrgOpenEventsBySite').default
  },
  org_device_reachability_by_site: {
    // eslint-disable-next-line global-require
    component: () => require('home/Organisation/widgets/OrgDeviceReachabilityBySite').default
  },
  org_device_health_rollup: {
    // eslint-disable-next-line global-require
    component: () => require('home/Organisation/widgets/OrgDeviceHealthRollup').default
  },
  org_cost_saved_by_site: {
    // eslint-disable-next-line global-require
    component: () => require('home/Organisation/widgets/OrgCostSavedBySite').default
  },
  org_site_scorecard: {
    // eslint-disable-next-line global-require
    component: () => require('home/Organisation/widgets/OrgSiteScorecard').default
  },
  open_closed_repairs: {
    sortOrder: ['open', 'completed'],
    isInteractive: true,
    handleClick: (id, filterOptions) => (data, switchAccount) => navToActiviyPage(id, data, filterOptions, switchAccount)
  },
  total_defects_detected: {
    ...defectWidgetOptions
  },
  active_defects_detected: {
    ...defectWidgetOptions
  },
  not_analysed_open_alarms: {
    isInteractive: true,
    handleClick: (id, filterOptions) => (data, switchAccount) => navToActiviyPage(id, data, filterOptions, switchAccount)
  },
  device_unreachable_above_fourteen_days_count: {
    sortOrder: ['unreachable'],
    formatData: true,
    filters: [
      { name: 'last_measurement', op: 'in', value: ['unreachable_extended'] },
    ],
    isInteractive: true,
    handleClick: (id, filterOptions, machineId, admindashboard) => (data, switchAccount) => navToDevicesPage(id, 0, filterOptions, switchAccount, admindashboard),
    tableMetadata: {
      modalTitle: 'Devices Unreachable Above 14 Days - Detailed View',
      selectedKeys: ['serial_numbers'],
      columnDefinitions: [
        { key: 'serial_number', displayName: 'Device Serial' },
        { key: 'timestamp', displayName: 'Last Reachable Timestamp', formatType: 'timestamp' },
        { key: 'account_name', displayName: 'Account Name' }
      ]
    }
  },
  devices_with_continuous_outliers_count: {
    sortOrder: ['total_count'],
    formatData: true,
    filters: [
      { name: 'other_filters', op: 'in', value: ['devices_with_continuous_outliers'] },
    ],
    isInteractive: true,
    handleClick: (id, filterOptions, machineId, admindashboard) => (data, switchAccount) => navToDevicesPage(id, 0, filterOptions, switchAccount, admindashboard),
    tableMetadata: {
      modalTitle: 'Devices with Continuous Outliers - Detailed View',
      selectedKeys: ['serial_numbers'],
      columnDefinitions: [
        { key: 'serial_number', displayName: 'Serial Number' },
        { key: 'account_name', displayName: 'Account' }
      ]
    }
  },
  devices_with_inconsistent_vibration_levels: {
    sortOrder: ['total_count'],
    formatData: true,
    filters: [
      { name: 'other_filters', op: 'in', value: ['devices_with_inconsistent_vibrations'] },
    ],
    isInteractive: true,
    handleClick: (id, filterOptions, machineId, admindashboard) => (data, switchAccount) => navToDevicesPage(id, 0, filterOptions, switchAccount, admindashboard),
    tableMetadata: {
      modalTitle: 'Devices with Inconsistent Vibration Levels - Detailed View',
      selectedKeys: ['serial_numbers'],
      columnDefinitions: [
        { key: 'serial_number', displayName: 'Serial Number' },
        { key: 'account_name', displayName: 'Account' }
      ]
    }
  },
  area_temperature_monitoring_table: {
    // eslint-disable-next-line global-require
    component: () => require('home/Dashboard/components/temperature_table/AreaTemperatureWidget').default
  },
  unread_events: {
    isInteractive: true,
    dataFormatter: formatUnreadEventsData,
    handleClick: (id, filterOptions) => data => navToActiviyPage(id, data, filterOptions)
  },
  total_cost_saved: {
    formatData: true,
    colors: [colors.green],
    isInteractive: true,
    handleClick: (id, filterOptions) => (data, switchAccount) =>
      navToActiviyPage(id, data, filterOptions, switchAccount)
  },
  cost_saved_by_machine: {
    orientation: 'horizontal',
    isInteractive: false,
    chartArea: { width: '75%', left: '15%' },
    dataFormatter: formatCostSavedByMachineData,
    yLabelFontSize: 8
  }
};
